import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  useTheme
} from '@mui/material';
import Loader from 'src/common/Loader';
import { useDispatch } from 'react-redux';
import { addNewSite, updateSiteDetails } from 'src/redux/siteSlice';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import {
  siteFormInitialValues,
  siteFormModel,
  siteValidationSchema
} from './siteFormModel';

const NewSite = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const formInitValues = initialValues || siteFormInitialValues;
  const {
    formId,
    formField: {
      siteName,
      siteCode,
      addressLine,
      city,
      state,
      country,
      pinCode,
      latitude,
      longitude
    }
  } = siteFormModel;

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateSiteDetails(values)).unwrap();
      } else {
        await dispatch(addNewSite(values)).unwrap();
      }
      handleClose();
      setLoading(false);
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Site updated successfully'
            : 'New site created successfully'
        })
      );
    } catch (error) {
      console.error('error', error);
      actions.setSubmitting(false);
      setLoading(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message || 'An error occurred' }));
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'EDIT SITE' : 'ADD NEW SITE'}
        onCancel={handleClose}
      />
      <Formik
        initialValues={formInitValues}
        validationSchema={siteValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form id={formId}>
                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={siteName.name}
                        label={siteName.label}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={siteCode.name}
                        label={siteCode.label}
                        upperCase
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={city.name}
                        label={city.label}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={pinCode.name}
                        label={pinCode.label}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={state.name}
                        label={state.label}
                        data={state.data}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={country.name}
                        label={country.label}
                        data={country.data}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={latitude.name}
                        label={latitude.label}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={longitude.name}
                        label={longitude.label}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputField
                        name={addressLine.name}
                        label={addressLine.label}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions
                  sx={{
                    px: 3,
                    py: 1.75,
                    mt: 1,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1.25,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 -8px 16px rgba(15, 23, 42, 0.06)',
                    bgcolor: 'rgba(255, 255, 255, 0.96)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                  >
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        minWidth: 110
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        minWidth: 130
                      }}
                    >
                      {initialValues ? 'Update Site' : 'Create Site'}
                    </Button>
                  </Stack>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewSite.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewSite;
